import { useEffect, useState, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import type { PluginListenerHandle } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type PermissionStatus = 'granted' | 'denied' | 'prompt';

interface UsePushNotificationsReturn {
  permissionStatus: PermissionStatus;
  isSupported: boolean;
  requestPermission: () => Promise<void>;
}

/**
 * Hook to manage push notifications for the SORIN mobile app.
 * Uses @capacitor/push-notifications for native iOS (APNs) and Android (FCM) support.
 * 
 * ARCHITECTURE: This hook uses TWO separate effects to avoid race conditions:
 * 1. Effect 1 (Listener Setup): Sets up listeners ONCE on mount, keeps them stable
 * 2. Effect 2 (Registration Trigger): Triggers registration when workerId is available
 * 
 * CRITICAL: Effect 1 has NO dependencies on functions that could change, preventing
 * the cleanup/re-setup cycle that was causing tokens to be lost.
 * 
 * @param workerId - The current worker's profile ID
 * @param onNotificationTap - Callback when user taps a notification
 */
export const usePushNotifications = (
  workerId: string | null,
  onNotificationTap?: (data: Record<string, string>) => void
): UsePushNotificationsReturn => {
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>('prompt');
  const [isSupported] = useState(() => Capacitor.isNativePlatform());
  const { toast } = useToast();

  // Refs for stable access across effects without triggering re-runs
  const workerIdRef = useRef<string | null>(null);
  const pendingTokenRef = useRef<string | null>(null);
  const toastRef = useRef(toast);
  const onNotificationTapRef = useRef(onNotificationTap);
  const listenersRef = useRef<PluginListenerHandle[]>([]);
  const listenersSetupRef = useRef(false);
  const hasRegisteredRef = useRef(false);

  // Keep callback refs updated
  useEffect(() => {
    toastRef.current = toast;
    onNotificationTapRef.current = onNotificationTap;
  }, [toast, onNotificationTap]);

  /**
   * EFFECT 1: Set up push notification listeners ONCE
   * 
   * CRITICAL: This effect has ONLY isSupported as a dependency.
   * All token saving logic is INLINED to avoid dependency on external functions.
   * This prevents the race condition where listeners were removed before the token arrived.
   */
  useEffect(() => {
    if (!isSupported) {
      console.log('[Push] Not on native platform, skipping listener setup');
      return;
    }

    // Prevent duplicate setup
    if (listenersSetupRef.current) {
      console.log('[Push] Listeners already set up, skipping');
      return;
    }

    let isMounted = true;

    const setupListeners = async () => {
      console.log('[Push] Setting up stable listeners...');
      listenersSetupRef.current = true;

      // Listen for successful registration - this gives us the token
      // INLINE all logic here to avoid external dependencies
      const registrationListener = await PushNotifications.addListener('registration', async (token: Token) => {
        if (!isMounted) {
          console.log('[Push] Component unmounted, ignoring token');
          return;
        }
        
        // Get platform directly here, not from closure
        const currentPlatform = Capacitor.getPlatform();
        const provider = currentPlatform === 'ios' ? 'ios-apns' : (currentPlatform === 'android' ? 'android-fcm' : 'web');
        const tokenField = provider === 'ios-apns' ? 'apns_token' : 'fcm_token';
        const currentWorkerId = workerIdRef.current;
        
        console.log(`[Push] ✅ Registration callback! Platform: ${currentPlatform}, Token: ${token.value.substring(0, 40)}...`);
        
        if (currentWorkerId) {
          // Save token directly to database - no external function call
          try {
            const update: Record<string, unknown> = {
              push_enabled: true,
              push_platform: provider,
              fcm_updated_at: new Date().toISOString()
            };
            update[tokenField] = token.value;

            const { error } = await supabase
              .from('worker_profiles')
              .update(update)
              .eq('id', currentWorkerId);

            if (error) {
              console.error('[Push] Failed to save token:', error);
            } else {
              console.log(`[Push] ✅ ${provider.toUpperCase()} token saved to worker_profiles!`);
            }
          } catch (err) {
            console.error('[Push] Error saving token:', err);
          }
        } else {
          // WorkerId not yet available, store for later
          console.log('[Push] WorkerId not ready, storing token for later');
          pendingTokenRef.current = token.value;
        }
      });
      listenersRef.current.push(registrationListener);
      console.log('[Push] ✓ Added registration listener');

      // Listen for registration errors
      const registrationErrorListener = await PushNotifications.addListener('registrationError', (error) => {
        console.error('[Push] ❌ Registration error:', error);
      });
      listenersRef.current.push(registrationErrorListener);

      // Listen for push notification received in foreground
      const notificationReceivedListener = await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
        console.log('[Push] Notification received in foreground:', notification.title);
        
        // Show in-app toast for foreground notifications
        toastRef.current({
          title: notification.title || 'SORIN',
          description: notification.body || '',
        });
      });
      listenersRef.current.push(notificationReceivedListener);

      // Listen for notification tap - use ref for callback
      const notificationActionListener = await PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
        console.log('[Push] Notification action performed:', action);
        
        const data = (action.notification.data || {}) as Record<string, string>;
        
        // Call custom handler if provided
        if (onNotificationTapRef.current) {
          onNotificationTapRef.current(data);
          return;
        }
        
        // Default navigation based on notification type
        if (data?.type === 'incident_alert' && data?.incident_id) {
          sessionStorage.setItem('pendingIncidentAlert', JSON.stringify(data));
          window.location.href = '/home?showIncidentAlert=true';
        } else if (data?.type === 'corroboration_request' && data?.report_id) {
          window.location.href = `/corroborate/${data.report_id}`;
        } else if (data?.type === 'quest_assigned' && data?.quest_id) {
          window.location.href = '/quests';
        } else if (data?.type === 'clarification_request' && data?.report_id) {
          window.location.href = `/report/${data.report_id}`;
        } else if (data?.type === 'team_alert') {
          window.location.href = '/';
        } else if (data?.type === 'badge_earned') {
          window.location.href = '/profile';
        } else if (data?.type === 'xp_awarded' && data?.report_id) {
          window.location.href = '/reported-history';
        } else if (data?.type === 'supervisor_task_assigned' && data?.task_id) {
          window.location.href = '/supervisor?tab=tasks';
        } else if (data?.type === 'coaching_assigned') {
          window.location.href = '/home';
        } else if (data?.type === 'hazard_resolved' && data?.report_id) {
          window.location.href = `/report/${data.report_id}`;
        } else if (data?.type === 'contextual_nudge') {
          window.location.href = '/home';
        } else if (data?.type === 'popup_intervention') {
          window.location.href = '/home';
        }
      });
      listenersRef.current.push(notificationActionListener);

      console.log('[Push] All 4 listeners set up successfully');
    };

    setupListeners();

    // Cleanup only on TRUE unmount
    return () => {
      console.log('[Push] Component unmounting, cleaning up listeners');
      isMounted = false;
      listenersSetupRef.current = false;
      hasRegisteredRef.current = false;
      
      for (const listener of listenersRef.current) {
        try {
          listener.remove();
        } catch (e) {
          console.warn('[Push] Error removing listener:', e);
        }
      }
      listenersRef.current = [];
    };
  }, [isSupported]); // ONLY isSupported - no function dependencies!

  /**
   * EFFECT 2: Handle workerId changes and trigger registration
   * 
   * This effect runs when workerId becomes available or changes.
   * It uses hasRegisteredRef to prevent duplicate registrations.
   */
  useEffect(() => {
    if (!isSupported) return;

    // Update ref so Effect 1's listeners can access current workerId
    workerIdRef.current = workerId;

    if (!workerId) {
      console.log('[Push] No workerId yet, waiting...');
      return;
    }

    console.log('[Push] WorkerId available:', workerId);

    // Check if we have a pending token from before workerId was ready
    if (pendingTokenRef.current) {
      console.log('[Push] Found pending token, saving now...');
      
      // Save the pending token
      const currentPlatform = Capacitor.getPlatform();
      const provider = currentPlatform === 'ios' ? 'ios-apns' : (currentPlatform === 'android' ? 'android-fcm' : 'web');
      const tokenField = provider === 'ios-apns' ? 'apns_token' : 'fcm_token';
      
      const update: Record<string, unknown> = {
        push_enabled: true,
        push_platform: provider,
        fcm_updated_at: new Date().toISOString()
      };
      update[tokenField] = pendingTokenRef.current;
      
      supabase
        .from('worker_profiles')
        .update(update)
        .eq('id', workerId)
        .then(({ error }) => {
          if (error) {
            console.error('[Push] Failed to save pending token:', error);
          } else {
            console.log(`[Push] ✅ Pending ${provider.toUpperCase()} token saved!`);
          }
        });
      
      pendingTokenRef.current = null;
    }

    // Prevent duplicate registrations
    if (hasRegisteredRef.current) {
      console.log('[Push] Already registered, skipping');
      return;
    }

    // Check current permission and register if needed
    const checkAndRegister = async () => {
      const permCheck = await PushNotifications.checkPermissions();
      console.log('[Push] Current permission status:', permCheck.receive);
      setPermissionStatus(permCheck.receive as PermissionStatus);

      if (permCheck.receive === 'prompt') {
        console.log('[Push] Permission is prompt, requesting...');
        hasRegisteredRef.current = true; // Set BEFORE async call
        
        const permResult = await PushNotifications.requestPermissions();
        
        if (permResult.receive === 'granted') {
          setPermissionStatus('granted');
          console.log('[Push] Permission granted, registering...');
          await PushNotifications.register();
        } else {
          setPermissionStatus('denied');
          console.log('[Push] Permission denied');
          toastRef.current({
            title: "Notifikasi Dinonaktifkan",
            description: "Anda tidak akan menerima peringatan keselamatan. Aktifkan di pengaturan perangkat.",
            variant: "destructive"
          });
        }
      } else if (permCheck.receive === 'granted') {
        console.log('[Push] Permission already granted, registering...');
        hasRegisteredRef.current = true;
        await PushNotifications.register();
      }
    };

    checkAndRegister();
  }, [isSupported, workerId]); // Only isSupported and workerId

  // Manual permission request (for UI buttons)
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.log('[Push] Not on native platform, skipping');
      return;
    }

    try {
      console.log('[Push] Manual permission request...');
      const permResult = await PushNotifications.requestPermissions();
      
      if (permResult.receive === 'granted') {
        setPermissionStatus('granted');
        await PushNotifications.register();
        console.log('[Push] Registered for push notifications');
      } else {
        setPermissionStatus('denied');
        console.log('[Push] Permission denied');
        toastRef.current({
          title: "Notifikasi Dinonaktifkan",
          description: "Anda tidak akan menerima peringatan keselamatan. Aktifkan di pengaturan perangkat.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('[Push] Permission request failed:', error);
    }
  }, [isSupported]);

  return {
    permissionStatus,
    isSupported,
    requestPermission
  };
};
